import { useDispatch, useSelector } from "react-redux";

import { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";

import { COLORS } from "@/constants/colors";
import {
  DEFAULT_STEP,
  PROFILING_LAYER_TYPES,
  ROW_ID_DETAIL_DATASET,
  SPATIAL_ANALYSIS_RESULT_DATASET_NAME,
  SPATIAL_TYPE_TO_LAYER_TYPE
} from "@/constants/dataset";
import { SUBLAYERS } from "@/constants/map";
import { RootState, store } from "@/store";
import {
  addDataset,
  addFilter,
  addRawDataset,
  cleanupVisualizationOrder,
  clearDataset,
  incrementDatasetCount,
  initializeWeightValues,
  removeDataset,
  removeRawDataset,
  reorderDatasets,
  resetDatasetToHistoryDataset,
  setDatasetIsFetching,
  setWeightValuesForDatasetId,
  toggleDatasetVisibility,
  updateDatasetColorStyle,
  updateDatasetGroupBy,
  updateDatasetH3Config,
  updateDatasetOpacity,
  updateDatasetSelectedCategory,
  updateDatasetStrokeAttribute,
  updateDatasetStrokeOpacity,
  updateDatasetStrokeWidth,
  updateDatasetStyleAttributeLabel,
  updateDatasetStyleColorLabel,
  updateDatasetStyleEnabledRank,
  updateDatasetStyleMaxRanking,
  updateDatasetStyleOpacityLabel,
  updateDatasetStyleVisibleLabel
} from "@/store/slice/datasetSlice";
import { setSelectedFeature } from "@/store/slice/mapSlice";
import { SpatialAggregationType } from "@/types/common";
import {
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetData,
  DatasetDetailResponse,
  DatasetFilter,
  DatasetHistory,
  DatasetIndices,
  DatasetMetadata,
  DatasetStyle,
  EColorLabel,
  EColorNames,
  H3Configuration,
  StyleConfig,
  WeightValueEntry
} from "@/types/dataset";
import { LayerType } from "@/types/map";
import { EOutputAnalysisType } from "@/types/spatial";
import { generateDatasetId } from "@/utils/common";
import {
  convertDatasetResponseToFeatureCollection,
  createDatasetAttributes,
  createDefaultSpatialDataset,
  createNewDataset,
  getDefaultSelectedCategory
} from "@/utils/dataset";
import { generateDatasetData } from "@/utils/generateDatasetData";
import {
  changeStrokeWidthLayer,
  getSourceProperties,
  toggleLayerVisibilityById
} from "@/utils/map";
import { wktToGeoJSON } from "@/utils/wkt";

import { useMap } from "./core/useMapContext";

type HandleAddDatasetType = {
  datasetMetadata: DatasetDetailResponse | DatasetMetadata;
  datasetData: DatasetData;
  savedFilters: DatasetFilter[];
  attributesSchema: DatasetAttributeSchema[];
  datasetIndex?: number;
  h3Configuration?: H3Configuration | null;
  weightValues?: {
    weight: string;
    attributes: Record<string, WeightValueEntry>;
  };
  withoutAttributeSchema?: boolean;
  styleConfig?: StyleConfig;
  index?: number;
  group_by?: string;
};

export const useDataset = () => {
  const dispatch = useDispatch();
  const {
    map,
    handleAddLineLayer,
    handleRemoveSourceLayer,
    handleAddPolygonLayer,
    handleChangeColorDataLayers,
    handleChangeColorLayers,
    handleChangeOpacityLayers,
    handleAddPointsLayer,
    handleAddGridLayer,
    handleAddSiteProfiling,
    handleResetLayerOrder,
    handleResetLayerData,
    removeAllCatchmentMarkers,
    showAllMarkers,
    hideAllMarkers,
    handleAddH3GeohashLayer,
    handleChangeVisibleLabelLayer,
    handleDisplayNameLabelLayerCategory,
    handleChangeLabelLayerColor,
    handleChangeLabelLayerOpacity,
    handleStyleRankEnabledChange,
    handleUpdateSourceWithRank,
    handleHighlightFeatureByGeometry
  } = useMap();

  const { datasets, rawDatasets, visualizationOrder, datasetIndices, history } =
    useSelector((state: RootState) => state.dataset);
  const { selectedFeature } = useSelector((state: RootState) => state.map);

  // Func to add dataset, and show the dataset in the map
  const handleAddDatasetAndFeatureData = (
    datasetIdOriginal: string,
    newDataset: Dataset,
    datasetIndex: number,
    savedFilter: DatasetFilter[],
    featureCollection: FeatureCollection<Geometry, GeoJsonProperties>,
    isCalculateWeight?: boolean
  ) => {
    dispatch(
      incrementDatasetCount({
        baseId: datasetIdOriginal,
        usedIndex: datasetIndex
      })
    );
    dispatch(addDataset({ dataset: newDataset, isCalculateWeight }));
    dispatch(cleanupVisualizationOrder());
    dispatch(addFilter({ datasetId: newDataset.data.id, filter: savedFilter }));

    const spatialType = newDataset.properties.spatialType;
    if (
      spatialType === LayerType.POLYGON ||
      spatialType === LayerType.ADMINISTRATIVE
    ) {
      handleAddPolygonLayer(newDataset, featureCollection);
    } else if (spatialType === LayerType.POINT) {
      handleAddPointsLayer(newDataset, featureCollection);
    } else if (
      spatialType === LayerType.H3 ||
      spatialType === LayerType.GEOHASH
    ) {
      handleAddH3GeohashLayer(newDataset, featureCollection);
    } else if (spatialType === LayerType.LINE) {
      handleAddLineLayer(newDataset, featureCollection);
    }
  };

  const handleAddDataset = async ({
    datasetMetadata,
    datasetData,
    savedFilters,
    attributesSchema,
    datasetIndex,
    h3Configuration,
    weightValues,
    withoutAttributeSchema,
    styleConfig,
    index,
    group_by
  }: HandleAddDatasetType) => {
    const { id, nextIndex } = datasetIndex
      ? {
          id: datasetMetadata.id + "_" + datasetIndex,
          nextIndex: datasetIndex
        }
      : generateDatasetId(datasetMetadata.id, datasetIndices);

    const featureCollection = convertDatasetResponseToFeatureCollection(
      datasetData,
      withoutAttributeSchema ? undefined : attributesSchema
    );

    const datasetName =
      nextIndex > 1
        ? `${datasetMetadata.name} (${nextIndex - 1})`
        : datasetMetadata.name;

    const resolvedDisplayName = datasetMetadata.display_name
      ? nextIndex > 1
        ? `${datasetMetadata.display_name} (${nextIndex - 1})`
        : datasetMetadata.display_name
      : datasetName;

    const newDataset = createNewDataset({
      dataset: datasetMetadata,
      datasetId: id,
      datasetName: resolvedDisplayName,
      attributesSchema,
      h3Configuration,
      styleConfig,
      totalRows: datasetData.rows.length,
      index: (index ?? 0) % 20,
      group_by
    });

    handleAddDatasetAndFeatureData(
      datasetMetadata.id,
      newDataset,
      nextIndex,
      savedFilters,
      featureCollection,
      !weightValues
    );

    dispatch(addRawDataset({ dataset: datasetData, id }));

    // Check if exist weight values for the dataset will be added, otherwise initialize weight values
    if (weightValues && Object.keys(weightValues).length > 0) {
      dispatch(
        setWeightValuesForDatasetId({
          datasetId: newDataset.data.id,
          data: weightValues
        })
      );
    } else dispatch(initializeWeightValues());

    return newDataset;
  };

  type HandleCreateDatasetHistoryType = {
    datasetMetadata: DatasetDetailResponse;
    datasetData: DatasetData;
    savedFilters: DatasetFilter[];
    attributesSchema: DatasetAttributeSchema[];
    datasetIndex?: number;
    h3Configuration?: H3Configuration | null;
    weightValues?: {
      weight: string;
      attributes: Record<string, WeightValueEntry>;
    };
    styleConfig?: StyleConfig;
    datasetHistory: DatasetHistory;
    datasetIndices: DatasetIndices;
    group_by?: string;
  };
  const handleCreateDatasetHistory = async ({
    datasetMetadata,
    datasetData,
    savedFilters,
    attributesSchema,
    h3Configuration,
    weightValues,
    styleConfig,
    datasetHistory,
    datasetIndices,
    group_by
  }: HandleCreateDatasetHistoryType) => {
    const { id, nextIndex } = generateDatasetId(
      datasetMetadata.id,
      datasetIndices
    );

    dispatch(
      incrementDatasetCount({
        baseId: datasetMetadata.id,
        usedIndex: nextIndex
      })
    );

    if (!datasetIndices[datasetMetadata.id]) {
      datasetIndices[datasetMetadata.id] = { counter: 0, usedIndices: [] };
    }
    datasetIndices[datasetMetadata.id].counter++;
    datasetIndices[datasetMetadata.id].usedIndices.push(nextIndex);

    const datasetName =
      nextIndex > 1
        ? `${datasetMetadata.name} (${nextIndex - 1})`
        : datasetMetadata.name;

    const newDataset = createNewDataset({
      dataset: datasetMetadata,
      datasetId: id,
      datasetName: datasetMetadata.display_name
        ? datasetMetadata.display_name
        : datasetName,
      attributesSchema,
      h3Configuration,
      styleConfig,
      group_by,
      totalRows: datasetData.rows.length
    });

    datasetHistory.datasets[newDataset.data.id] = newDataset;
    datasetHistory.rawDatasets[newDataset.data.id] = datasetData;
    datasetHistory.visualizationOrder.unshift(newDataset.data.id);

    dispatch(
      addFilter({ datasetId: newDataset.data.id, filter: savedFilters })
    );
    if (weightValues && Object.keys(weightValues).length > 0) {
      dispatch(
        setWeightValuesForDatasetId({
          datasetId: newDataset.data.id,
          data: weightValues
        })
      );
    }

    return newDataset;
  };

  /** Function handler to add deleted datasets into the Redux Store `datasets` slice */
  const handleAddDeletedDataset = async (
    datasetMetadata: DatasetDetailResponse,
    datasetIndex: number
  ) => {
    const datasetIdWithIndex = `${datasetMetadata.id}_${datasetIndex}`;

    /** Dataset name that is displayed in the Dataset List e.g. `Population by province (1)` */
    const datasetNameWithIndex =
      datasetIndex > 1
        ? `${datasetMetadata.name} (${datasetIndex - 1})`
        : datasetMetadata.name;

    const newDeletedDataset = createNewDataset({
      dataset: datasetMetadata,
      datasetId: datasetIdWithIndex,
      datasetName: datasetMetadata.display_name
        ? datasetMetadata.display_name
        : datasetNameWithIndex,
      attributesSchema: []
    });

    dispatch(addDataset({ dataset: newDeletedDataset }));
    dispatch(
      incrementDatasetCount({
        baseId: datasetMetadata.id,
        usedIndex: datasetIndex
      })
    );
    dispatch(cleanupVisualizationOrder());
  };

  /** Function handler to add datasets by passing dataset and its weight values */
  const handleAddDatasetAndWeightValues = async (
    datasetMetadata: DatasetDetailResponse,
    datasetIndex: number,
    datasetData: DatasetData,
    savedFilter: DatasetFilter[],
    attributesSchema: DatasetAttributeSchema[],
    weightValues: {
      weight: string;
      attributes: Record<string, WeightValueEntry>;
    },
    styleConfig?: StyleConfig
  ) => {
    const featureCollection = convertDatasetResponseToFeatureCollection(
      datasetData,
      attributesSchema
    );
    const datasetId = `${datasetMetadata.id}_${datasetIndex}`;
    const datasetName =
      datasetIndex > 1
        ? `${datasetMetadata.name} (${datasetIndex - 1})`
        : datasetMetadata.name;
    const newDataset = createNewDataset({
      dataset: datasetMetadata,
      datasetId,
      datasetName: datasetMetadata.display_name
        ? datasetMetadata.display_name
        : datasetName,
      attributesSchema: attributesSchema,
      styleConfig,
      totalRows: datasetData.rows.length
    });

    handleAddDatasetAndFeatureData(
      datasetMetadata.id,
      newDataset,
      datasetIndex,
      savedFilter,
      featureCollection
    );

    // Check if exist weight values for the dataset will be added, otherwise initialize weight values
    if (Object.keys(weightValues).length > 0) {
      dispatch(
        setWeightValuesForDatasetId({
          datasetId: newDataset.data.id,
          data: weightValues
        })
      );
    } else dispatch(initializeWeightValues());

    dispatch(addRawDataset({ dataset: datasetData, id: datasetId }));
  };

  const handleClearDataset = () => {
    const visualizationOrder = store.getState().dataset.visualizationOrder;

    visualizationOrder.forEach((datasetId) => {
      const dataset = datasets[datasetId];
      if (dataset?.data?.available)
        handleRemoveSourceLayer(
          dataset.data.id,
          dataset.properties.spatialType
        );
    });

    // Clear all datasets in the store, to avoid duplicate datasets
    dispatch(clearDataset());
  };

  const handleAddSpatialAnalysisDatasetByType = ({
    spatialType,
    newDataset,
    featureCollection
  }: {
    spatialType: string;
    newDataset: Dataset;
    featureCollection: FeatureCollection;
  }) => {
    // Handle different spatial types
    if (
      spatialType === LayerType.POLYGON ||
      spatialType === LayerType.ADMINISTRATIVE
    ) {
      handleAddPolygonLayer(newDataset, featureCollection);
    } else if (spatialType === LayerType.POINT) {
      handleAddPointsLayer(newDataset, featureCollection);
    } else if (spatialType === EOutputAnalysisType.GRID) {
      handleAddGridLayer(newDataset, featureCollection);
    } else if (spatialType === EOutputAnalysisType.PROFILING) {
      // Site profiling can have both point and polygon geometries
      // Check the geometry types in the feature collection to determine how to handle them
      handleAddSiteProfiling(newDataset, featureCollection);
    } else if (
      spatialType === LayerType.H3 ||
      spatialType === LayerType.GEOHASH
    ) {
      handleAddH3GeohashLayer(newDataset, featureCollection);
    } else if (spatialType === LayerType.LINE) {
      handleAddLineLayer(newDataset, featureCollection);
    }
  };

  const handleAddSpatialAnalysisDataset = async (
    data: DatasetData,
    schema: DatasetAttributeSchema[] = [],
    isPreviewMode: boolean = false // When in preview mode, the dataset should be visible
  ) => {
    const attributes = createDatasetAttributes(data.columns);
    const defaultCategory = getDefaultSelectedCategory(
      attributes,
      data.spatial_aggregation_type
    );

    const filteredAttributes = attributes.filter(
      (attr) => attr.attribute_name !== ROW_ID_DETAIL_DATASET
    );
    const filteredSchema = schema?.filter(
      (attr) => attr.is_visible && attr.attribute_name !== ROW_ID_DETAIL_DATASET
    );
    const featureCollection = convertDatasetResponseToFeatureCollection(
      data,
      filteredSchema
    );

    const newDataset: Dataset = {
      data: {
        schema,
        id: data.dataset_id,
        name: data.name,
        display_name: data.display_name ?? data.name,
        spatial_aggregation_type: data.spatial_aggregation_type,
        country_id: "id",
        source: "bvt",
        short_description: "",
        published: true,
        created_at: "2021-01-01",
        updated_at: "2021-01-01",
        dataset_size_kb: data.dataset_size_kb,
        available: true,
        total_rows: data.rows.length
      },
      properties: {
        isVisible:
          isPreviewMode ||
          data.spatial_aggregation_type === EOutputAnalysisType.GRID ||
          data.spatial_aggregation_type === EOutputAnalysisType.PROFILING,
        spatialType: data.spatial_aggregation_type
      },
      style: {
        weight: 1,
        opacity: 1,
        color: "#fff",
        fillColor: COLORS.TEAL[500],
        colorFamily: EColorNames.TEAL,
        fillOpacity: 0.7,
        visibility:
          isPreviewMode ||
          data.spatial_aggregation_type === EOutputAnalysisType.GRID ||
          data.spatial_aggregation_type === EOutputAnalysisType.PROFILING
            ? "visible"
            : "none",
        colorRanges: [],
        stroke: {
          attribute_id: defaultCategory?.id ?? "",
          attribute_name: defaultCategory?.attribute_name ?? "",
          width: 1,
          opacity: 0.7
        },
        labelConfig: {
          color_scale: EColorLabel.BLACK_WHITE,
          attribute_id: defaultCategory?.id ?? "",
          attribute_name: defaultCategory?.attribute_name ?? "",
          show_label: false,
          opacity: 0.7
        },
        rankConfig: {
          isEnabled: false,
          maxRanking: 10
        },
        colorSteps: DEFAULT_STEP
      },
      categories: filteredAttributes,
      selectedCategory: defaultCategory ?? {
        id: "",
        attribute_name: "",
        data_type: DataSchemaType.INT,
        is_nullable: true,
        is_spatial: false,
        position: 0,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      }
    };
    dispatch(addDataset({ dataset: newDataset }));
    dispatch(addRawDataset({ dataset: data, id: data.dataset_id }));

    // Handle different spatial types
    handleAddSpatialAnalysisDatasetByType({
      spatialType: data.spatial_aggregation_type,
      newDataset,
      featureCollection
    });
  };

  const addSpatialAnalysisDataset = async (data: DatasetData) => {
    const featureCollection = convertDatasetResponseToFeatureCollection(data);
    const attributes = createDatasetAttributes(data.columns);

    const newDataset: Dataset = createDefaultSpatialDataset({
      data,
      attributes,
      isAnalysisResult: data.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME // Check if this is a spatial analysis result
    });
    dispatch(addDataset({ dataset: newDataset }));
    dispatch(addRawDataset({ dataset: data, id: data.dataset_id }));

    const spatialType = data.spatial_aggregation_type;

    if (spatialType === EOutputAnalysisType.GRID) {
      handleAddGridLayer(newDataset, featureCollection);
    } else if (spatialType === EOutputAnalysisType.PROFILING) {
      // Site profiling can have both point and polygon geometries
      // Check the geometry types in the feature collection to determine how to handle them
      handleAddSiteProfiling(newDataset, featureCollection);
    } else if (
      spatialType === LayerType.H3 ||
      spatialType === LayerType.GEOHASH
    ) {
      handleAddH3GeohashLayer(newDataset, featureCollection);
    } else if (spatialType === LayerType.LINE) {
      handleAddLineLayer(newDataset, featureCollection);
    }
  };

  const addNonSpatialAnalysisDataset = async (data: DatasetData) => {
    const attributes = createDatasetAttributes(data.columns);
    const newDataset = createDefaultSpatialDataset({
      data,
      attributes,
      isAnalysisResult: true
    });

    dispatch(addDataset({ dataset: newDataset }));
    dispatch(addRawDataset({ dataset: data, id: data.dataset_id }));
  };

  const addAnalysisDatasetGeometry = async (data: DatasetData) => {
    dispatch(
      setDatasetIsFetching({ datasetId: data.dataset_id, isFetching: false })
    );
    dispatch(addRawDataset({ dataset: data, id: data.dataset_id }));
  };

  const handleRemoveDataset = (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => {
    dispatch(removeDataset(datasetId));
    dispatch(removeRawDataset(datasetId));
    handleRemoveSourceLayer(datasetId, spatialType);
    // Reinitialize weight values across existing datasets after removing a dataset
    dispatch(initializeWeightValues());
    // Check and remove selected feature if it belongs to the removed dataset
    if (selectedFeature?.source === datasetId) {
      dispatch(setSelectedFeature(null));
    }
  };

  const handleToggleDatasetVisibility = (
    datasetId: string,
    showLabel: boolean = false,
    isRankEnabled: boolean = false,
    forceVisibility?: boolean
  ) => {
    if (!map) return;

    const dataset = datasets[datasetId];

    if (!dataset || !dataset.properties) return;

    const isNewLayer = !getSourceProperties(map, datasetId);

    if (isNewLayer) {
      const rawDataset = rawDatasets[datasetId];

      if (!dataset.data) return;

      const featureCollection =
        convertDatasetResponseToFeatureCollection(rawDataset);
      const spatialType = dataset.data.spatial_aggregation_type;

      if (
        spatialType === LayerType.POLYGON ||
        spatialType === LayerType.ADMINISTRATIVE
      ) {
        handleAddPolygonLayer(dataset, featureCollection);
      } else if (spatialType === LayerType.POINT) {
        handleAddPointsLayer(dataset, featureCollection);
      } else if (
        spatialType === LayerType.H3 ||
        spatialType === LayerType.GEOHASH
      ) {
        handleAddH3GeohashLayer(dataset, featureCollection);
      } else if (spatialType === LayerType.LINE) {
        handleAddLineLayer(dataset, featureCollection);
      }

      const layersByDatasetId: { [datasetId: string]: string[] } = {};

      for (let j = 0; j < visualizationOrder.length; j++) {
        const vizDatasetId = visualizationOrder[j];

        if (
          vizDatasetId !== datasetId &&
          !getSourceProperties(map, vizDatasetId)
        ) {
          continue;
        }

        const layers =
          SUBLAYERS(vizDatasetId)[
            datasets[vizDatasetId].properties.spatialType
          ];

        if (layers) {
          layersByDatasetId[vizDatasetId] = Object.values(layers);
        }
      }

      const datasetIds = Object.keys(layersByDatasetId);

      for (let i = datasetIds.length - 1; i >= 0; i--) {
        const datasetId = datasetIds[i];
        const layers = layersByDatasetId[datasetId];

        layers.forEach((layerId) => {
          map.moveLayer(layerId);
        });
      }
    }

    const { isVisible } = dataset.properties;
    const spatial_aggregation_type = dataset.data?.spatial_aggregation_type;

    if (isVisible === undefined) return;

    const newVisibility =
      forceVisibility ?? (isNewLayer ? isVisible : !isVisible);

    if (newVisibility !== isVisible || isNewLayer) {
      toggleLayerVisibilityById(
        map,
        datasetId,
        newVisibility,
        showLabel,
        isRankEnabled
      );

      if (spatial_aggregation_type === EOutputAnalysisType.PROFILING) {
        if (newVisibility) showAllMarkers();
        else hideAllMarkers();
      }

      if (newVisibility !== isVisible) {
        dispatch(
          toggleDatasetVisibility({ datasetId, visibility: newVisibility })
        );
      }
    }
  };

  const handleReorderDataset = (datasetId: string, newIndex: number) => {
    const currentIndex = visualizationOrder.indexOf(datasetId);

    if (currentIndex === -1) return;

    const newVisualizationOrder = [...visualizationOrder];

    newVisualizationOrder.splice(currentIndex, 1);
    newVisualizationOrder.splice(newIndex, 0, datasetId);

    dispatch(reorderDatasets(newVisualizationOrder));

    if (!map) return;
    const style = map.getStyle();

    if (!style?.layers) return;

    const layersByDatasetId: Record<string, string[]> = {};

    style.layers.forEach((layer) => {
      if (!("source" in layer) || !layer.source) return;

      const owningDatasetId = newVisualizationOrder.find((id) =>
        layer.source.startsWith(id)
      );

      if (!owningDatasetId) return;

      if (!layersByDatasetId[owningDatasetId]) {
        layersByDatasetId[owningDatasetId] = [];
      }

      layersByDatasetId[owningDatasetId].push(layer.id);
    });

    for (let i = newVisualizationOrder.length - 1; i >= 0; i--) {
      const id = newVisualizationOrder[i];
      const layers = layersByDatasetId[id] ?? [];

      layers.forEach((layerId) => {
        map.moveLayer(layerId);
      });
    }
  };

  const handleChangeSelectedCategoryDataset = (
    dataset: Dataset,
    selectedCategoryName: string,
    colorFamily: EColorNames,
    step: number = 10
  ) => {
    const selectedCategoryMetadata = dataset.categories.find(
      (category) => category.attribute_name === selectedCategoryName
    );

    if (
      dataset.data.spatial_aggregation_type === EOutputAnalysisType.PROFILING
    ) {
      PROFILING_LAYER_TYPES.forEach((type) => {
        handleChangeColorDataLayers(
          dataset,
          selectedCategoryMetadata as DatasetAttributeSchema,
          colorFamily,
          step,
          type
        );
      });
    } else {
      handleChangeColorDataLayers(
        dataset,
        selectedCategoryMetadata as DatasetAttributeSchema,
        colorFamily,
        step || 10
      );
    }

    dispatch(
      updateDatasetSelectedCategory({
        datasetId: dataset.data.id,
        selectedCategoryName
      })
    );
  };

  const handleChangeColorDataset = (
    datasetId: string,
    colorFamily: EColorNames,
    colorRanges: {
      min?: number | null;
      max?: number | null;
      value?: string | null;
      color: string;
      label: string;
    }[],
    selectedCategoryName: string,
    spatialType: SpatialAggregationType,
    dataType: DataSchemaType,
    steps?: number
  ) => {
    handleChangeColorLayers(
      datasetId,
      colorFamily,
      colorRanges,
      selectedCategoryName,
      spatialType,
      dataType,
      steps
    );

    dispatch(updateDatasetColorStyle({ datasetId, colorFamily }));
  };

  const handleChangeStrokeWidthDataset = (datasetId: string, width: number) => {
    const dataset = datasets[datasetId];

    if (!dataset) return;

    const layerType =
      SPATIAL_TYPE_TO_LAYER_TYPE[dataset.properties.spatialType];
    const sublayers = SUBLAYERS(datasetId)[layerType];

    changeStrokeWidthLayer(map, sublayers, width);

    dispatch(updateDatasetStrokeWidth({ datasetId, width }));
  };

  const handleChangeStrokeAttributeDataset = (
    dataset: Dataset,
    attributeName: string,
    colorFamily: EColorNames,
    step: number = 10
  ) => {
    const attribute = dataset.categories.find(
      (cat) => cat.attribute_name === attributeName
    );
    if (!attribute) return;

    // Update layers similar to handleChangeSelectedCategoryDataset
    if (
      dataset.data.spatial_aggregation_type === EOutputAnalysisType.PROFILING
    ) {
      PROFILING_LAYER_TYPES.forEach((type) => {
        handleChangeColorDataLayers(
          dataset,
          attribute,
          colorFamily,
          step,
          type
        );
      });
    } else {
      handleChangeColorDataLayers(dataset, attribute, colorFamily, step);
    }

    dispatch(
      updateDatasetStrokeAttribute({
        datasetId: dataset.data.id,
        attributeId: attribute.id,
        attributeName: attribute.attribute_name
      })
    );
  };

  const handleChangeOpacityDataset = (datasetId: string, opacity: number) => {
    const dataset = datasets[datasetId];

    if (!dataset) return;

    handleChangeOpacityLayers(
      datasetId,
      opacity,
      dataset.properties.spatialType
    );

    dispatch(updateDatasetOpacity({ datasetId, opacity }));
  };

  const handleChangeStrokeOpacityDataset = (
    datasetId: string,
    opacity: number
  ) => {
    const dataset = datasets[datasetId];

    if (!dataset) return;

    handleChangeOpacityLayers(
      datasetId,
      opacity,
      dataset.properties.spatialType
    );

    dispatch(updateDatasetStrokeOpacity({ datasetId, opacity }));
  };

  const handleResetDatasetToHistoryDataset = () => {
    removeAllCatchmentMarkers();
    dispatch(resetDatasetToHistoryDataset());
    handleResetLayerOrder(history.visualizationOrder);
    handleResetLayerData(history.datasets, history.rawDatasets);
  };

  const handleUpdateDatasetData = async (
    datasetId: string,
    data: DatasetData,
    h3Configuration: H3Configuration | null = null,
    groupBy?: string
  ) => {
    const featureCollection = convertDatasetResponseToFeatureCollection(data);
    if (!datasets?.[datasetId]) return;
    //  Remove source layer from the map
    const dataset = datasets[datasetId];
    // Remove old source layer
    handleRemoveSourceLayer(dataset.data.id, dataset.properties.spatialType);
    // Add new source layer
    const spatialType = data.spatial_aggregation_type;
    if (
      spatialType === LayerType.POLYGON ||
      spatialType === LayerType.ADMINISTRATIVE
    ) {
      handleAddPolygonLayer(dataset, featureCollection);
    } else if (spatialType === LayerType.POINT) {
      handleAddPointsLayer(dataset, featureCollection);
    } else if (
      spatialType === LayerType.H3 ||
      spatialType === LayerType.GEOHASH
    ) {
      handleAddH3GeohashLayer(dataset, featureCollection);
    } else if (spatialType === LayerType.LINE) {
      handleAddLineLayer(dataset, featureCollection);
    } else if (spatialType === EOutputAnalysisType.PROFILING) {
      handleAddSiteProfiling(dataset, featureCollection);
    }
    if (h3Configuration)
      dispatch(updateDatasetH3Config({ datasetId, config: h3Configuration }));

    if (groupBy) dispatch(updateDatasetGroupBy({ datasetId, groupBy }));

    dispatch(addRawDataset({ dataset: data, id: dataset.data.id }));
  };

  const handleDisplayNameVisibility = (
    dataset: Dataset,
    visibleLabel: boolean
  ) => {
    dispatch(
      updateDatasetStyleVisibleLabel({
        datasetId: dataset.data.id,
        visible: visibleLabel
      })
    );
    const layerLabelId = `${dataset.data.id}-label`;
    handleChangeVisibleLabelLayer(layerLabelId, visibleLabel);
  };

  const handleDisplayName = (
    dataset: Dataset,
    selectedCategory?: DatasetAttributeSchema
  ) => {
    handleDisplayNameLabelLayerCategory(
      `${dataset.data.id}-label`,
      selectedCategory as DatasetAttributeSchema
    );
    dispatch(
      updateDatasetStyleAttributeLabel({
        datasetId: dataset.data.id,
        attribute_id: selectedCategory?.id || "",
        attribute_name: selectedCategory?.attribute_name || ""
      })
    );
  };

  const handleDisplayNameColor = (
    dataset: Dataset,
    selectedColor: EColorLabel
  ) => {
    handleChangeLabelLayerColor(`${dataset.data.id}-label`, selectedColor);
    handleChangeLabelLayerColor(`${dataset.data.id}-rank`, selectedColor);
    dispatch(
      updateDatasetStyleColorLabel({
        datasetId: dataset.data.id,
        color: selectedColor
      })
    );
  };

  const handleChangeLabelOpacity = (dataset: Dataset, opacity: number) => {
    handleChangeLabelLayerOpacity(`${dataset.data.id}-label`, opacity);
    handleChangeLabelLayerOpacity(`${dataset.data.id}-rank`, opacity);
    dispatch(
      updateDatasetStyleOpacityLabel({
        datasetId: dataset.data.id,
        opacity: opacity
      })
    );
  };

  const handleRankConfigChange = async (
    datasetId: string,
    style: DatasetStyle,
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    }
  ) => {
    if (!map) return;

    // check for custom id polygon source (site profiling analysis result)
    const sourceId = map.getSource(`${datasetId}-polygon`)
      ? `${datasetId}-polygon`
      : datasetId;

    // update source maplibre
    handleUpdateSourceWithRank({
      datasetId: sourceId,
      style,
      update
    });

    // update redux store
    if (update.isEnabled !== undefined) {
      const payload = { datasetId, isEnabled: update.isEnabled };
      handleStyleRankEnabledChange({
        ...payload,
        isShowLabel: style.labelConfig.show_label
      });
      dispatch(updateDatasetStyleEnabledRank(payload));
    }

    if (update.maxRanking !== undefined) {
      const payload = { datasetId, maxRanking: update.maxRanking };
      dispatch(updateDatasetStyleMaxRanking(payload));
    }
  };

  const handleHighlightDatasetFeatureByWkt = ({
    datasetId = null,
    value = null,
    type = "wkt"
  }: {
    datasetId: string | null;
    value: string | GeoJsonProperties | null;
    type?: "grid" | "wkt";
  }) => {
    if (!map || !datasetId || !value) return;

    let geometry: Geometry | null = null;

    if (type === "wkt") {
      const geometryFeature = wktToGeoJSON(value as string);
      if (!geometryFeature?.geometry) return;
      geometry = geometryFeature.geometry;
    } else if (type === "grid") {
      const feature = value as GeoJsonProperties;
      if (!feature?.geometry) return;
      geometry = feature.geometry as Geometry;
    }

    handleToggleDatasetVisibility(datasetId, false, false, true);
    handleReorderDataset(datasetId, 0);
    if (!geometry) return;

    return handleHighlightFeatureByGeometry(datasetId, geometry);
  };

  return {
    handleClearDataset,
    handleRemoveDataset,
    handleAddDataset,
    handleAddDeletedDataset,
    handleAddSpatialAnalysisDataset,
    handleAddSpatialAnalysisDatasetByType,
    handleChangeStrokeWidthDataset,
    handleChangeStrokeAttributeDataset,
    handleChangeSelectedCategoryDataset,
    handleToggleDatasetVisibility,
    handleReorderDataset,
    handleChangeColorDataset,
    handleChangeOpacityDataset,
    handleChangeStrokeOpacityDataset,
    handleResetDatasetToHistoryDataset,
    handleAddDatasetAndWeightValues,
    handleUpdateDatasetData,
    addSpatialAnalysisDataset,
    addNonSpatialAnalysisDataset,
    addAnalysisDatasetGeometry,
    generateDatasetData,
    handleDisplayNameVisibility,
    handleDisplayName,
    handleDisplayNameColor,
    handleChangeLabelOpacity,
    handleCreateDatasetHistory,
    handleRankConfigChange,
    handleHighlightDatasetFeatureByWkt
  };
};
